import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  Image as ImageIcon,
  Type,
  Link as LinkIcon,
  MapPin,
  KeyRound,
  Upload,
  Save,
  RotateCcw,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const SUB_TABS = [
  { id: "images", label: "Images", icon: ImageIcon },
  { id: "text", label: "Text", icon: Type },
  { id: "links", label: "Links", icon: LinkIcon },
  { id: "neighborhoods", label: "Neighborhoods", icon: MapPin },
  { id: "credentials", label: "Credentials", icon: KeyRound },
];

const IMAGE_SLOTS = [
  {
    key: "portraitKey",
    label: "About-page portrait",
    hint: "The tall headshot on /about. Portrait orientation (4:5) works best.",
    fallback: "/tammy-headshot.jpg",
    aspect: "aspect-[4/5]",
    accept: "image/*",
  },
  {
    key: "homeBannerKey",
    label: "Home-page banner",
    hint: "Brand strip above 'One life. Many threads.' Wide format (~1750×700).",
    fallback: "/tammy-banner.jpg",
    aspect: "aspect-[875/350]",
    accept: "image/*",
  },
  {
    key: "cornerstoneFlyerKey",
    label: "Cornerstone flyer",
    hint: "Downloadable PNG/PDF on /leadership-coaching. Portrait orientation.",
    fallback: "/cornerstone-coaches-guide.png",
    aspect: "aspect-[3/4]",
    accept: "image/*,.pdf",
  },
];

const TEXT_FIELDS = [
  {
    path: "about.signature",
    label: "About — signature line",
    hint: "The large italic quote at the bottom of the About page.",
    default: "Be the person who stays.",
  },
];

const LINK_FIELDS = [
  { path: "links.kwMlsUrl", label: "KW MLS search URL", default: "https://tammyrichard.kw.com/" },
  { path: "links.facebookPage", label: "Tammy's Facebook page", default: "https://www.facebook.com/tammyricharKW" },
  { path: "links.facebookGroup", label: "Business coaching Facebook group", default: "https://www.facebook.com/groups/1492779424358054" },
  { path: "links.kwMaineResources", label: "KW Maine resources site", default: "https://www.kwmaineresources.com/" },
  { path: "orientation.zoomUrl", label: "Orientation Zoom URL", default: "https://us06web.zoom.us/j/84979359487?pwd=w9tVwwmgQsW8GGcbb3V2InbGqGwWRS.1" },
  { path: "orientation.zoomId", label: "Orientation Zoom ID", default: "849 7935 9487" },
  { path: "orientation.zoomPasscode", label: "Orientation Zoom passcode", default: "8900" },
];

const CRED_FIELDS = [
  { path: "credentials.adminPassword", label: "Admin dashboard password", type: "password", note: "Applies after next reload. Update `ADMIN_PASSWORD` in production env for permanence." },
  { path: "credentials.coachingPassword", label: "Coaching portal password (shared)", type: "text", note: "Applies after next reload. Update `COACHING_PASSWORD` in production env for permanence." },
  { path: "credentials.senderEmail", label: "Resend sender (From address)", type: "text", note: "e.g., `Tammy Richard <tammy@mreamomentum.com>`." },
  { path: "credentials.adminEmail", label: "Where form emails go", type: "email", note: "Recipient for booking/inquiry notifications." },
];

const NEIGHBORHOODS = [
  { slug: "portland", name: "Portland" },
  { slug: "south-portland", name: "South Portland" },
  { slug: "cape-elizabeth", name: "Cape Elizabeth" },
  { slug: "falmouth", name: "Falmouth" },
  { slug: "cumberland", name: "Cumberland" },
  { slug: "yarmouth", name: "Yarmouth" },
  { slug: "scarborough", name: "Scarborough" },
  { slug: "westbrook", name: "Westbrook" },
  { slug: "gorham", name: "Gorham" },
  { slug: "freeport", name: "Freeport" },
  { slug: "brunswick", name: "Brunswick" },
  { slug: "windham", name: "Windham" },
];

const NB_FIELDS = [
  { k: "medianPrice", label: "Median price", type: "number" },
  { k: "daysOnMarket", label: "Days on market", type: "number" },
  { k: "pricePerSqft", label: "$ / sq ft", type: "number" },
  { k: "newListingsCount", label: "Active listings", type: "number" },
  { k: "yoy", label: "YoY %", type: "number" },
];

function get(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function set(obj, path, value) {
  const parts = path.split(".");
  const copy = { ...obj };
  let cur = copy;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return copy;
}

export default function ContentTab({ authHeaders, onSaved }) {
  const [sub, setSub] = useState("images");
  const [draft, setDraft] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/site-settings", { headers: authHeaders });
      setSaved(res.data || {});
      setDraft(res.data || {});
    } catch {
      toast.error("Could not load site settings.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...draft };
      // Never send `updated_at`
      delete body.updated_at;
      await api.put("/site-settings", body, { headers: authHeaders });
      setSaved(body);
      // Bust the global cache so public pages pick up new values
      try {
        delete window.__tr_site_settings__;
      } catch {
        // ignore
      }
      toast.success("Saved. Live on the site.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => setDraft(saved);

  if (loading) {
    return <div className="p-16 text-center text-foreground/55">Loading content…</div>;
  }

  return (
    <div className="p-6" data-testid="content-tab">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-1 bg-muted rounded-full p-1 w-fit mb-6">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const active = sub === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              data-testid={`content-subtab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-full transition-all ${
                active
                  ? "bg-primary text-primary-foreground shadow"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {sub === "images" && <ImagesPanel draft={draft} setDraft={setDraft} authHeaders={authHeaders} />}
      {sub === "text" && <TextPanel draft={draft} setDraft={setDraft} />}
      {sub === "links" && <LinksPanel draft={draft} setDraft={setDraft} />}
      {sub === "neighborhoods" && <NeighborhoodsPanel draft={draft} setDraft={setDraft} />}
      {sub === "credentials" && <CredentialsPanel draft={draft} setDraft={setDraft} />}

      {/* Sticky save bar */}
      <div className="sticky bottom-4 mt-10 flex justify-end gap-3 bg-card border border-border rounded-2xl p-4 shadow-lg" data-testid="content-save-bar">
        <Button
          onClick={discard}
          disabled={!dirty || saving}
          variant="outline"
          className="rounded-full h-10"
          data-testid="content-discard-btn"
        >
          <RotateCcw size={14} className="mr-2" />
          Discard changes
        </Button>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-full bg-primary hover:bg-primary/90 h-10"
          data-testid="content-save-btn"
        >
          <Save size={14} className="mr-2" />
          {saving ? "Saving…" : dirty ? "Save changes" : "All changes saved"}
        </Button>
      </div>
    </div>
  );
}

/* ---------- IMAGES ---------- */

function mediaUrlForPreview(key, fallback) {
  if (!key) return fallback;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/site-media/${key}`;
}

function ImagesPanel({ draft, setDraft, authHeaders }) {
  return (
    <div className="space-y-6">
      {IMAGE_SLOTS.map((slot) => (
        <ImageSlot
          key={slot.key}
          slot={slot}
          value={get(draft, `images.${slot.key}`)}
          onChange={(v) => setDraft(set(draft, `images.${slot.key}`, v))}
          authHeaders={authHeaders}
        />
      ))}
    </div>
  );
}

function ImageSlot({ slot, value, onChange, authHeaders }) {
  const [uploading, setUploading] = useState(false);
  const src = mediaUrlForPreview(value, slot.fallback);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8 MB).");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await api.post("/admin/upload?subdir=media", fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.key);
      toast.success(`${slot.label} uploaded — remember to save.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div
      className="bg-card border border-border rounded-2xl p-6 flex flex-col md:flex-row gap-6"
      data-testid={`image-slot-${slot.key}`}
    >
      <div className={`w-full md:w-64 shrink-0 ${slot.aspect} bg-muted rounded-xl overflow-hidden border border-border`}>
        {src && (
          <img
            src={src}
            alt={slot.label}
            className="w-full h-full object-cover"
            data-testid={`image-preview-${slot.key}`}
          />
        )}
      </div>
      <div className="flex-1">
        <p className="font-serif text-2xl leading-tight">{slot.label}</p>
        <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{slot.hint}</p>
        {value && (
          <p className="text-[11px] uppercase tracking-[0.18em] text-foreground/50 mt-3">
            Current key: <span className="normal-case tracking-normal font-mono text-xs">{value}</span>
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-3">
          <label className="inline-flex" data-testid={`image-upload-label-${slot.key}`}>
            <input
              type="file"
              accept={slot.accept}
              onChange={onFile}
              className="hidden"
              data-testid={`image-upload-input-${slot.key}`}
            />
            <span
              className={`inline-flex items-center rounded-full h-10 px-5 text-sm font-medium border border-primary bg-primary text-primary-foreground cursor-pointer hover:bg-primary/90 ${
                uploading ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              <Upload size={14} className="mr-2" />
              {uploading ? "Uploading…" : "Upload new"}
            </span>
          </label>
          {value && (
            <Button
              variant="outline"
              className="rounded-full h-10"
              onClick={() => onChange(null)}
              data-testid={`image-reset-${slot.key}`}
            >
              Reset to default
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- TEXT ---------- */

function TextPanel({ draft, setDraft }) {
  return (
    <div className="space-y-6">
      {TEXT_FIELDS.map((f) => (
        <div key={f.path} className="bg-card border border-border rounded-2xl p-6" data-testid={`text-field-${f.path}`}>
          <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">
            {f.label}
          </Label>
          <p className="text-xs text-foreground/60 mt-1">{f.hint}</p>
          <Textarea
            value={get(draft, f.path) ?? ""}
            onChange={(e) => setDraft(set(draft, f.path, e.target.value))}
            placeholder={f.default}
            rows={3}
            className="mt-3 rounded-xl"
          />
          <p className="text-[10px] text-foreground/50 mt-2">
            Default: <span className="italic">{f.default}</span>
          </p>
        </div>
      ))}
      <p className="text-xs text-foreground/55">
        More editable fields can be added later — anywhere on the site, tell me which text you want to control from here.
      </p>
    </div>
  );
}

/* ---------- LINKS ---------- */

function LinksPanel({ draft, setDraft }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {LINK_FIELDS.map((f) => {
        const val = get(draft, f.path) ?? "";
        return (
          <div
            key={f.path}
            className="bg-card border border-border rounded-2xl p-5"
            data-testid={`link-field-${f.path}`}
          >
            <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">
              {f.label}
            </Label>
            <Input
              value={val}
              onChange={(e) => setDraft(set(draft, f.path, e.target.value))}
              placeholder={f.default}
              className="mt-2 rounded-xl h-11"
            />
            {val && val.startsWith("http") && (
              <a
                href={val}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary hover:underline"
              >
                Open <ExternalLink size={11} />
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- NEIGHBORHOODS ---------- */

function NeighborhoodsPanel({ draft, setDraft }) {
  const [selected, setSelected] = useState(NEIGHBORHOODS[0].slug);
  const path = `neighborhoodOverrides.${selected}`;
  const current = get(draft, path) || {};

  const update = (k, v) => {
    const next = { ...current, [k]: v === "" ? null : Number(v) };
    // Clean out nulls
    Object.keys(next).forEach((key) => next[key] == null && delete next[key]);
    if (Object.keys(next).length === 0) {
      // Remove the override entirely
      const parts = path.split(".");
      const parent = get(draft, parts[0]) || {};
      const cleaned = { ...parent };
      delete cleaned[parts[1]];
      setDraft(set(draft, parts[0], cleaned));
    } else {
      setDraft(set(draft, path, next));
    }
  };

  const reset = () => {
    const parts = path.split(".");
    const parent = get(draft, parts[0]) || {};
    const cleaned = { ...parent };
    delete cleaned[parts[1]];
    setDraft(set(draft, parts[0], cleaned));
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid="neighborhoods-panel">
      <div className="flex flex-wrap gap-2 mb-6">
        {NEIGHBORHOODS.map((n) => (
          <button
            key={n.slug}
            onClick={() => setSelected(n.slug)}
            data-testid={`neighborhood-pick-${n.slug}`}
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${
              selected === n.slug
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent border-border text-foreground/70 hover:text-foreground"
            }`}
          >
            {n.name}
          </button>
        ))}
      </div>
      <p className="text-xs text-foreground/60 mb-5">
        Leave a field blank to fall back to the live RentCast number. Filled fields override RentCast for that town.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {NB_FIELDS.map((f) => (
          <div key={f.k}>
            <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">
              {f.label}
            </Label>
            <Input
              type="number"
              step="any"
              value={current[f.k] ?? ""}
              onChange={(e) => update(f.k, e.target.value)}
              placeholder="RentCast"
              className="mt-2 rounded-xl h-11"
              data-testid={`nb-input-${selected}-${f.k}`}
            />
          </div>
        ))}
      </div>
      {Object.keys(current).length > 0 && (
        <Button
          variant="outline"
          className="rounded-full h-9 mt-6"
          onClick={reset}
          data-testid={`nb-reset-${selected}`}
        >
          Clear all overrides for {selected}
        </Button>
      )}
    </div>
  );
}

/* ---------- CREDENTIALS ---------- */

function CredentialsPanel({ draft, setDraft }) {
  return (
    <div className="space-y-4">
      {CRED_FIELDS.map((f) => (
        <div key={f.path} className="bg-card border border-border rounded-2xl p-5" data-testid={`cred-field-${f.path}`}>
          <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">
            {f.label}
          </Label>
          <Input
            type={f.type}
            value={get(draft, f.path) ?? ""}
            onChange={(e) => setDraft(set(draft, f.path, e.target.value))}
            className="mt-2 rounded-xl h-11"
            placeholder="—"
          />
          <p className="text-xs text-foreground/60 mt-2">{f.note}</p>
        </div>
      ))}
      <p className="text-xs text-foreground/60">
        Note: values here are stored in the database and become the active runtime values on the next backend restart. The `.env` file still holds the original defaults, which are only used if the DB has nothing set.
      </p>
    </div>
  );
}
